import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, InputNumber, Select, Spin, Empty, Modal, Avatar, Tag, Divider, Popconfirm } from "antd";
import {
    GlobalOutlined, AppstoreOutlined, ShopOutlined,
    PercentageOutlined, EyeOutlined, CheckCircleFilled,
    InfoCircleOutlined, SaveOutlined, ThunderboltOutlined, EditOutlined, CloseOutlined,
    UserOutlined, MailOutlined, PhoneOutlined, DeleteOutlined,
} from "@ant-design/icons";
import SafeImage from "../../component/SafeImage";
import PricingRuleServ from "../../service/PricingRuleServ";
import CategoryServ from "../../service/CategoryServ";
import ShopServ from "../../service/ShopServ";
import Notify from "../../util/Notify";
import Breadcrumbs from "../../component/Breadcrumbs";

const { Option } = Select;

const TYPE_META = {
    global: { icon: <GlobalOutlined />, label: "Global Level", color: "#FF8110", tagColor: "#c2410c", tagBg: "#fff7ed" },
    category: { icon: <AppstoreOutlined />, label: "Category Level", color: "#3b82f6", tagColor: "#1d4ed8", tagBg: "#eff6ff" },
    shop: { icon: <ShopOutlined />, label: "Shop Level", color: "#8b5cf6", tagColor: "#6d28d9", tagBg: "#f5f3ff" },
};
const PRIORITY_LABEL = { shop: "High", category: "Medium", global: "Standard" };
const PRIORITY_COLOR = { shop: "#8b5cf6", category: "#3b82f6", global: "#FF8110" };

const card = {
    background: "rgba(255,255,255,0.82)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    borderRadius: 20,
    border: "1.5px solid rgba(255,129,16,0.12)",
    boxShadow: "0 8px 32px rgba(41,5,101,0.08)",
    overflow: "hidden",
    marginBottom: 24,
};

const cardHeaderPurple = {
    background: "linear-gradient(135deg, #0B1F4D 0%, #4A1A8A 100%)",
    padding: "18px 24px",
    display: "flex", alignItems: "center", gap: 12,
};
const cardHeaderGreen = {
    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    padding: "18px 24px",
    display: "flex", alignItems: "center", gap: 12,
};
const headerIcon = {
    width: 38, height: 38, borderRadius: 11,
    background: "rgba(255,255,255,0.15)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 17, color: "#FF8110",
};
const headerTitle = { margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 };
const headerSub = { margin: "2px 0 0", color: "rgba(255,255,255,0.6)", fontSize: 12 };

const label = { fontSize: 13, fontWeight: 600, color: "#0B1F4D", marginBottom: 8, display: "block" };
const req = { color: "#FF8110", marginRight: 3 };
const divider = { height: 1, background: "linear-gradient(90deg,transparent,rgba(41,5,101,0.1),transparent)", margin: "18px 0" };

/* ── table ── */
const thCell = {
    padding: "11px 16px", fontSize: 11, fontWeight: 700, color: "#0B1F4D",
    textTransform: "uppercase", letterSpacing: 0.7,
    borderBottom: "1.5px solid #ede9f6", whiteSpace: "nowrap", textAlign: "left",
};
const tdCell = {
    padding: "13px 16px", fontSize: 13, color: "#1a1a2e",
    borderBottom: "1px solid #f4f0fb", verticalAlign: "middle",
};

const pill = (color, bg) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 10px", borderRadius: 20, fontSize: 10,
    fontWeight: 700, color, background: bg,
    letterSpacing: 0.5, textTransform: "uppercase",
});

const ProductSettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [editingRule, setEditingRule] = useState(null); // rule being edited
    const [infoModalVisible, setInfoModalVisible] = useState(false);
    const [selectedShop, setSelectedShop] = useState(null);
    const formCardRef = useRef(null);

    const [categories, setCategories] = useState([]);
    const [parentCategories, setParentCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [shops, setShops] = useState([]);
    const [allRules, setAllRules] = useState([]);

    const [targetType, setTargetType] = useState("global");
    const [selectedParentId, setSelectedParentId] = useState(null);
    const [selectedSubId, setSelectedSubId] = useState(null);
    const [selectedShopId, setSelectedShopId] = useState(null);

    const [previewSellerPrice] = useState(100);
    const markupValue = Form.useWatch("value", form) || 0;
    const previewMarkup = Math.round(previewSellerPrice * (markupValue / 100));
    const previewBuyerPrice = useMemo(
        () => Math.round(previewSellerPrice * (1 + markupValue / 100)),
        [previewSellerPrice, markupValue]
    );

    useEffect(() => {
        (async () => {
            setPageLoading(true);
            try {
                const [catRes, shopRes, ruleRes] = await Promise.all([
                    CategoryServ.index({ limit: 1000 }),
                    ShopServ.adminIndex({ limit: 1000 }),
                    PricingRuleServ.getAll(),
                ]);
                if (catRes?.data) { setCategories(catRes.data); setParentCategories(catRes.data.filter(c => !c.parentId)); }
                if (shopRes?.data) setShops(shopRes.data);
                if (ruleRes?.data) setAllRules(ruleRes.data);
            } catch { Notify.error("Failed to load configuration data"); }
            finally { setPageLoading(false); }
        })();
    }, []);

    useEffect(() => {
        let rule = null;
        if (targetType === "global") rule = allRules.find(r => r.type === "global");
        else if (targetType === "category") {
            const id = selectedSubId || selectedParentId;
            if (id) rule = allRules.find(r => r.type === "category" && r.categoryId?._id === id);
        } else if (targetType === "shop" && selectedShopId) {
            rule = allRules.find(r => r.type === "shop" && r.shopId?._id === selectedShopId);
        }
        form.setFieldsValue({ value: rule ? rule.markupPercentage : 0 });
    }, [targetType, selectedParentId, selectedSubId, selectedShopId, allRules, form]);

    const onParentChange = val => {
        setSelectedParentId(val); setSelectedSubId(null);
        setSubCategories(val ? categories.filter(c => c.parentId === val) : []);
    };

    const onFinish = async values => {
        if (targetType === "category" && !(selectedSubId || selectedParentId)) { Notify.error("Please select a category"); return; }
        if (targetType === "shop" && !selectedShopId) { Notify.error("Please select a shop"); return; }
        setLoading(true);
        try {
            await PricingRuleServ.upsert({
                type: targetType,
                markupPercentage: values.value,
                categoryId: targetType === "category" ? (selectedSubId || selectedParentId) : null,
                shopId: targetType === "shop" ? selectedShopId : null,
            });
            Notify.success("Pricing configuration saved successfully");
            setEditingRule(null);
            const ruleRes = await PricingRuleServ.getAll();
            if (ruleRes?.data) setAllRules(ruleRes.data);
        } catch { Notify.error("Failed to save pricing settings"); }
        finally { setLoading(false); }
    };

    const getRuleDisplay = rule => {
        if (rule.type === "global") return "Global (All Products)";
        if (rule.type === "category") return `${rule.categoryId?.name || "Removed Category"} Category`;
        if (rule.type === "shop") return `${rule.shopId?.name || "Removed Shop"} Shop`;
        return "Unknown";
    };

    return (
        <div style={{ background: "linear-gradient(135deg,#f8f4ff,#fff7ed,#f0f9ff)", minHeight: "100vh", padding: 24, fontFamily: "'Instacart Sans',Arial,sans-serif" }}>
            <div style={{ marginBottom: 20 }}><Breadcrumbs /></div>

            <Spin spinning={pageLoading}>

                {/* ═══════ TOP — Configure Pricing (full width) ═══════ */}
                <div style={card} ref={formCardRef}>
                    {/* Header: purple normally, amber banner when editing */}
                    <div style={editingRule ? {
                        background: "linear-gradient(135deg, #92400e 0%, #b45309 100%)",
                        padding: "18px 24px", display: "flex", alignItems: "center", gap: 12,
                    } : cardHeaderPurple}>
                        <div style={headerIcon}><PercentageOutlined /></div>
                        <div>
                            <p style={headerTitle}>
                                {editingRule ? `Editing: ${getRuleDisplay(editingRule)}` : "Configure Pricing"}
                            </p>
                            <p style={headerSub}>
                                {editingRule ? "Update the markup below and click Save Settings" : "Set markup rules at any level"}
                            </p>
                        </div>
                        {editingRule && (
                            <button
                                onClick={() => {
                                    setEditingRule(null);
                                    setTargetType("global");
                                    setSelectedParentId(null); setSelectedSubId(null); setSelectedShopId(null);
                                    form.setFieldsValue({ value: 0 });
                                }}
                                style={{
                                    marginLeft: "auto", background: "rgba(255,255,255,0.18)",
                                    border: "1.5px solid rgba(255,255,255,0.35)",
                                    borderRadius: 8, color: "#fff", padding: "4px 12px",
                                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 6,
                                }}
                            >
                                <CloseOutlined /> Cancel Edit
                            </button>
                        )}
                    </div>

                    <div style={{ padding: 24 }}>
                        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ value: 0 }}>
                            {/* Row 1 — dynamic columns based on targetType */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns:
                                    targetType === "global" ? "1fr 1fr" :
                                        targetType === "shop" ? "1fr 1fr 1fr" :
                                            "1fr 1fr 1fr 1fr",  /* category: 4 cols */
                                gap: 16,
                                alignItems: "start",
                            }}>

                                {/* Configuration Level */}
                                <div>
                                    <span style={label}>Configuration Level</span>
                                    <Select value={targetType} size="large" style={{ width: "100%" }}
                                        onChange={val => { setTargetType(val); setSelectedParentId(null); setSelectedSubId(null); setSelectedShopId(null); }}>
                                        {Object.entries(TYPE_META).map(([k, m]) => (
                                            <Option key={k} value={k}>
                                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <span style={{ color: m.color }}>{m.icon}</span>{m.label}
                                                </span>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>

                                {/* Parent Category — only visible when targetType === 'category' */}
                                {targetType === "category" && (
                                    <div>
                                        <span style={label}><span style={req}>*</span>Parent Category</span>
                                        <Select placeholder="Select parent category" value={selectedParentId}
                                            onChange={onParentChange} showSearch optionFilterProp="children"
                                            size="large" style={{ width: "100%" }} allowClear>
                                            {parentCategories.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
                                        </Select>
                                    </div>
                                )}

                                {/* Sub-Category — only visible when targetType === 'category' */}
                                {targetType === "category" && (
                                    <div>
                                        <span style={label}>Sub-Category <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span></span>
                                        <Select placeholder="All sub-categories" value={selectedSubId} onChange={setSelectedSubId}
                                            allowClear disabled={!selectedParentId}
                                            showSearch optionFilterProp="children" size="large" style={{ width: "100%" }}>
                                            {subCategories.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
                                        </Select>
                                    </div>
                                )}

                                {/* Shop — only visible when targetType === 'shop' */}
                                {targetType === "shop" && (
                                    <div>
                                        <span style={label}><span style={req}>*</span>Select Shop</span>
                                        <Select placeholder="Select a shop" value={selectedShopId} onChange={setSelectedShopId}
                                            showSearch optionFilterProp="children" size="large" style={{ width: "100%" }} allowClear>
                                            {shops.map(s => <Option key={s._id} value={s._id}>{s.name}</Option>)}
                                        </Select>
                                    </div>
                                )}

                                {/* Markup % */}
                                <div>
                                    <span style={label}><span style={req}>*</span>Markup Percentage (%)</span>
                                    <Form.Item
                                        name="value"
                                        style={{ margin: 0 }}
                                        rules={[
                                            { required: true, message: "Please enter a markup percentage" },
                                            { type: "number", min: 0, message: "Markup cannot be negative" },
                                            { type: "number", max: 20, message: "Maximum allowed markup is 20%" },
                                        ]}
                                        help={
                                            markupValue > 20
                                                ? <span style={{ color: "#dc2626", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>⚠️ Maximum allowed markup is 20%. Please reduce.</span>
                                                : markupValue > 0
                                                    ? <span style={{ color: "#16a34a", fontSize: 12, fontWeight: 500 }}>✓ Valid markup</span>
                                                    : null
                                        }
                                        validateStatus={markupValue > 20 ? "error" : markupValue > 0 ? "success" : ""}
                                    >
                                        <InputNumber
                                            min={0}
                                            size="large"
                                            style={{ width: "100%" }}
                                            placeholder="e.g. 10"
                                            formatter={v => `${v}%`}
                                            parser={v => v.replace("%", "")}
                                        />
                                    </Form.Item>
                                </div>
                            </div>

                            <div style={divider} />

                            {/* Row 2 — Live Preview + Save button */}
                            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                                {/* Live Preview */}
                                <div style={{
                                    flex: 1, background: "linear-gradient(135deg,#fff7ed,#fef9f0)",
                                    border: "1.5px dashed rgba(255,129,16,0.4)", borderRadius: 14, padding: "16px 24px",
                                    display: "flex", alignItems: "center", gap: 0,
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#FF8110", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.8, marginRight: 20 }}>
                                        <EyeOutlined /> Live Preview
                                    </div>
                                    {[
                                        { label: "Seller Price", value: `₹${previewSellerPrice}`, color: "#0B1F4D" },
                                        { op: "+" },
                                        { label: `Markup (${markupValue}%)`, value: `₹${previewMarkup}`, color: "#FF8110" },
                                        { op: "=" },
                                        { label: "Buyer Price", value: `₹${previewBuyerPrice}`, color: "#16a34a" },
                                    ].map((item, i) => item.op ? (
                                        <div key={i} style={{ fontSize: 20, color: "#d1d5db", padding: "0 12px" }}>{item.op}</div>
                                    ) : (
                                        <div key={i} style={{ textAlign: "center" }}>
                                            <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>{item.label}</div>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>{item.value}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Save Button */}
                                <button type="submit" disabled={loading}
                                    style={{
                                        height: 50, padding: "0 36px", borderRadius: 14, border: "none",
                                        background: loading ? "#f3e8ff" : "linear-gradient(135deg,#FF8110,#DE6E00)",
                                        color: loading ? "#0B1F4D" : "#fff", fontSize: 15, fontWeight: 700,
                                        cursor: loading ? "not-allowed" : "pointer",
                                        display: "flex", alignItems: "center", gap: 10,
                                        boxShadow: loading ? "none" : "0 4px 18px rgba(255,129,16,0.35)",
                                        transition: "all 0.2s ease", whiteSpace: "nowrap",
                                    }}
                                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(255,129,16,0.45)"; } }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 18px rgba(255,129,16,0.35)"; }}>
                                    {loading ? <><ThunderboltOutlined spin /> Saving…</> : <><SaveOutlined /> Save Settings</>}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>

                {/* ═══════ BOTTOM — Active Configurations Table ═══════ */}
                <div style={card}>
                    <div style={cardHeaderGreen}>
                        <div style={{ ...headerIcon, color: "#fff", background: "rgba(255,255,255,0.18)" }}>
                            <CheckCircleFilled />
                        </div>
                        <div>
                            <p style={headerTitle}>Active Configurations</p>
                            <p style={headerSub}>{allRules.length} rule{allRules.length !== 1 ? "s" : ""} configured</p>
                        </div>
                        {/* Summary pills */}
                        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                            {[
                                { label: `${allRules.find(r => r.type === "global")?.markupPercentage ?? 0}% Global`, color: "#FF8110", bg: "rgba(255,129,16,0.15)" },
                                { label: `${allRules.filter(r => r.type === "category").length} Category`, color: "#60a5fa", bg: "rgba(96,165,250,0.15)" },
                                { label: `${allRules.filter(r => r.type === "shop").length} Shop`, color: "#c084fc", bg: "rgba(192,132,252,0.15)" },
                            ].map((s, i) => (
                                <span key={i} style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, color: s.color, background: s.bg }}>
                                    {s.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div style={{ padding: "0 0 24px" }}>
                        {(() => {
                            const visibleRules = allRules.filter(r => r.markupPercentage > 0);
                            return visibleRules.length > 0 ? (
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr style={{ background: "linear-gradient(135deg,#f5f0ff,#fff0e6)" }}>
                                            <th style={{ ...thCell, width: 50, textAlign: "center" }}>#</th>
                                            <th style={thCell}>Rule Name</th>
                                            <th style={{ ...thCell, textAlign: "center" }}>Level</th>
                                            <th style={{ ...thCell, textAlign: "center" }}>Markup %</th>
                                            <th style={{ ...thCell, textAlign: "center" }}>Priority</th>
                                            <th style={{ ...thCell, textAlign: "center" }}>Scope</th>
                                            <th style={{ ...thCell, textAlign: "center" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleRules.map((rule, idx) => {
                                            const meta = TYPE_META[rule.type] || TYPE_META.global;
                                            const pColor = PRIORITY_COLOR[rule.type] || "#888";
                                            const scope = rule.type === "category"
                                                ? (rule.categoryId?.name || "Removed Category")
                                                : rule.type === "shop"
                                                    ? (rule.shopId?.name || "Removed Shop")
                                                    : "All Products";
                                            const isH = hoveredRow === rule._id;
                                            return (
                                                <tr key={rule._id}
                                                    style={{ background: isH ? `${meta.color}08` : "#fff", transition: "background 0.18s" }}
                                                    onMouseEnter={() => setHoveredRow(rule._id)}
                                                    onMouseLeave={() => setHoveredRow(null)}>
                                                    <td style={{ ...tdCell, textAlign: "center", color: "#bbb", fontWeight: 700 }}>{idx + 1}</td>
                                                    <td style={tdCell}>
                                                        {rule.type === 'shop' && rule.shopId ? (
                                                            <span
                                                                style={{ fontWeight: 700, fontSize: 14, color: '#0B1F4D', cursor: 'pointer', transition: 'all 0.2s' }}
                                                                onMouseEnter={e => e.currentTarget.style.color = '#FF8110'}
                                                                onMouseLeave={e => e.currentTarget.style.color = '#0B1F4D'}
                                                                onClick={() => {
                                                                    const fullShop = shops.find(s => s._id === rule.shopId?._id);
                                                                    setSelectedShop(fullShop || rule.shopId);
                                                                    setInfoModalVisible(true);
                                                                }}
                                                            >
                                                                {getRuleDisplay(rule)}
                                                            </span>
                                                        ) : (
                                                            <span style={{ fontWeight: 700, fontSize: 14 }}>{getRuleDisplay(rule)}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ ...tdCell, textAlign: "center" }}>
                                                        <span style={pill(meta.tagColor, meta.tagBg)}>
                                                            {meta.icon}&nbsp;{rule.type.charAt(0).toUpperCase() + rule.type.slice(1)}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdCell, textAlign: "center" }}>
                                                        <span style={{ fontSize: 18, fontWeight: 800, color: meta.color }}>{rule.markupPercentage}%</span>
                                                    </td>
                                                    <td style={{ ...tdCell, textAlign: "center" }}>
                                                        <span style={pill(pColor, `${pColor}15`)}>{PRIORITY_LABEL[rule.type]}</span>
                                                    </td>
                                                    <td style={{ ...tdCell, textAlign: "center", fontSize: 12, color: "#6b7280" }}>
                                                        {rule.type === 'shop' && rule.shopId ? (
                                                            <Tag
                                                                color="purple"
                                                                style={{ cursor: 'pointer', fontWeight: 600 }}
                                                                onClick={() => {
                                                                    const fullShop = shops.find(s => s._id === rule.shopId?._id);
                                                                    setSelectedShop(fullShop || rule.shopId);
                                                                    setInfoModalVisible(true);
                                                                }}
                                                            >
                                                                {scope}
                                                            </Tag>
                                                        ) : scope}
                                                    </td>
                                                    <td style={{ ...tdCell, textAlign: "center" }}>
                                                        <button
                                                            onClick={() => {
                                                                // Pre-fill form with this rule's values
                                                                setTargetType(rule.type);
                                                                if (rule.type === "category") {
                                                                    const parentId = rule.categoryId?.parentId || rule.categoryId?._id;
                                                                    setSelectedParentId(parentId);
                                                                    setSelectedSubId(rule.categoryId?.parentId ? rule.categoryId._id : null);
                                                                    setSubCategories(categories.filter(c => c.parentId === parentId));
                                                                } else if (rule.type === "shop") {
                                                                    setSelectedShopId(rule.shopId?._id);
                                                                } else {
                                                                    setSelectedParentId(null);
                                                                    setSelectedSubId(null);
                                                                    setSelectedShopId(null);
                                                                }
                                                                form.setFieldsValue({ value: rule.markupPercentage });
                                                                window.scrollTo({ top: 0, behavior: "smooth" });
                                                            }}
                                                            style={{
                                                                padding: "5px 14px", borderRadius: 8, border: `1.5px solid ${meta.color}`,
                                                                background: `${meta.color}12`, color: meta.color,
                                                                fontSize: 12, fontWeight: 700, cursor: "pointer",
                                                                transition: "all 0.18s ease",
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = meta.color; e.currentTarget.style.color = "#fff"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = `${meta.color}12`; e.currentTarget.style.color = meta.color; }}
                                                        >
                                                            ✏️ Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: "56px 0", textAlign: "center" }}>
                                    <Empty description="No active pricing rules found" />
                                </div>
                            );
                        })()}

                        {/* Priority info */}
                        <div style={{
                            margin: "20px 24px 0",
                            background: "linear-gradient(135deg,#fffbe6,#fff9f0)",
                            border: "1.5px solid rgba(255,129,16,0.2)",
                            borderRadius: 14, padding: "14px 18px",
                        }}>
                            <div style={{ color: "#856404", fontWeight: 700, fontSize: 13, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                <InfoCircleOutlined style={{ color: "#FF8110" }} /> Priority Rules
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#a16207", lineHeight: 1.9 }}>
                                <li><strong>Shop Level</strong> — overrides everything else.</li>
                                <li><strong>Category Level</strong> — applies if no Shop rule exists.</li>
                                <li><strong>Global Level</strong> — catch-all fallback.</li>
                                <li>No rules? Products show the original <strong>Seller Price</strong>.</li>
                            </ul>
                        </div>
                    </div>
                </div>

            </Spin>

            {/* Shop & Seller Details Modal */}
            <Modal
                open={infoModalVisible}
                onCancel={() => setInfoModalVisible(false)}
                footer={null}
                width={600}
                centered
                closable={false}
                bodyStyle={{ padding: 0 }}
                maskStyle={{ backdropFilter: 'blur(3px)' }}
                style={{ borderRadius: 20, overflow: 'hidden' }}
            >
                {selectedShop && (
                    <div style={{ background: '#fff', borderRadius: 20, overflow: 'hidden' }}>
                        {/* Header Section - Premium Analysis Style */}
                        <div style={{ 
                            padding: '16px 20px', 
                            background: 'linear-gradient(135deg, #f0f7ff 0%, #f5f3ff 100%)', 
                            borderBottom: '1px solid #e2e8f0', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 14, 
                            position: 'relative' 
                        }}>
                            <button 
                                onClick={() => setInfoModalVisible(false)}
                                style={{ position: 'absolute', right: 16, top: 16, background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                            >
                                <CloseOutlined />
                            </button>
                            
                            <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0, background: '#fff', boxShadow: '0 4px 10px rgba(0,0,0,0.04)' }}>
                                <SafeImage src={selectedShop.imageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            
                            <div style={{ flex: 1 }}>
                                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>{selectedShop.name}</h2>
                                <p style={{ margin: '1px 0 4px', fontSize: 11, color: '#64748b', fontWeight: 600 }}>Shop ID: <span style={{ color: '#4f46e5' }}>{selectedShop.shopId || selectedShop._id}</span></p>
                                <Tag color="warning" style={{ borderRadius: 5, padding: '0 8px', fontSize: 9, fontWeight: 700, border: 'none', background: '#fff', color: '#c2410c', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                    {selectedShop.business?.category || 'Shop'}
                                </Tag>
                            </div>
                        </div>

                        {/* Detail Grid Section - Compact */}
                        <div style={{ padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: '#fff' }}>
                            {/* Location - Full Width */}
                            <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, letterSpacing: '0.4px' }}>Location</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 600, lineHeight: 1.3 }}>
                                    {selectedShop.location ? `${selectedShop.location.address || ''}, ${selectedShop.location.city || ''}, ${selectedShop.location.state || ''}` : 'Location Not Provided'}
                                </div>
                            </div>

                            {/* Times */}
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Opening Time</div>
                                <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
                                    {selectedShop.business?.openingTime || '08:00 AM'}
                                </div>
                            </div>
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Closing Time</div>
                                <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 700 }}>
                                    {selectedShop.business?.closingTime || '09:00 PM'}
                                </div>
                            </div>

                            {/* Contact */}
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Phone</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedShop.contact?.phone || 'N/A'}</div>
                            </div>
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Email</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedShop.contact?.email || 'N/A'}</div>
                            </div>

                            {/* Business IDs */}
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>GST Number</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedShop.business?.gstNumber || 'N/A'}</div>
                            </div>
                            <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Shop License Number</div>
                                <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedShop.business?.shopLicenseNumber || 'N/A'}</div>
                            </div>

                            {/* Status */}
                            <div style={{ gridColumn: 'span 2', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
                                <Tag color="success" style={{ margin: 0, fontWeight: 800, borderRadius: 5, border: '1px solid #dcfce7', background: '#f0fdf4', color: '#16a34a', fontSize: 10 }}>
                                    {(selectedShop.approvalStatus || 'APPROVED').toUpperCase()}
                                </Tag>
                            </div>

                            {/* Description - Full Width */}
                            <div style={{ gridColumn: 'span 2', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Description</div>
                                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, lineHeight: 1.4 }}>
                                    {selectedShop.description || 'This is a nice shop.'}
                                </div>
                            </div>
                        </div>

                        {/* Footer Section - Compact */}
                        <div style={{ padding: '12px 16px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button 
                                    style={{
                                        background: 'none', border: '1px solid #e2e8f0', color: '#3b82f6', fontSize: 12, fontWeight: 700,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6
                                    }}
                                    onClick={() => {
                                        const rule = allRules.find(r => r.shopId?._id === selectedShop._id);
                                        if (rule) {
                                            setTargetType(rule.type);
                                            setSelectedShopId(rule.shopId?._id);
                                            form.setFieldsValue({ value: rule.markupPercentage });
                                            window.scrollTo({ top: 0, behavior: "smooth" });
                                        }
                                        setInfoModalVisible(false);
                                    }}
                                >
                                    <EditOutlined /> Edit Config
                                </button>
                            </div>

                            <button 
                                onClick={() => setInfoModalVisible(false)}
                                style={{
                                    padding: '6px 20px', borderRadius: 8, border: '1px solid #e2e8f0',
                                    background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 700,
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ProductSettings;
